import Icon from '@/components/Icon';
import styles from './Conversas.module.css';
import {
  mdiAccountAlert,
  mdiAlertCircle,
  mdiCheckAll,
  mdiCheckCircle,
  mdiEyeOff,
  mdiHeadset,
  mdiInformationOutline,
  mdiLoading,
  mdiRobot,
} from '@mdi/js';
import React, { useEffect } from 'react';
import Badge from '../../components/layout/Badge';
import ChatDto from '@tera/shared/src/dto/ChatDto';
import Markdown from './Markdown';
import { chatKey } from './chatList';
import { useRascunhos } from './rascunhos';
import TextoData from '../../components/tipografia/TextoData';
import InfiniteScroll from 'react-infinite-scroller';
import { BadgeMarcadores } from './BadgeMarcadores';
import { useQuery } from 'react-query';
import axios from 'axios';
import MarcadoresSm from '@/components/layout/MarcadoresSm';
import HBox from '@/components/layout/HBox';
import AlertasAtendimento from './AlertasAtendimento';
import AlertasChamada from './AlertasChamada';
import AlertBox from '@/components/layout/AlertBox';
import { useAuth } from '@/lib/useAuth';

const iconeStatus: Record<string, React.ReactNode> = {
  chatbot: (
    <Icon path={mdiRobot} size={1.25} color="var(--tc-color-gray-500)" />
  ),
  em_atendimento: (
    <Icon path={mdiHeadset} size={1.25} color="var(--tc-color-gray-500)" />
  ),
  finalizado: (
    <Icon path={mdiCheckCircle} size={1.25} color="var(--tc-color-gray-500)" />
  ),
};

type ConversasProps = {
  value: ChatDto[];
  /** Identidade (`chatKey`) da conversa aberta, ou null se nenhuma. */
  selectedKey: string | null;
  /** Recebe o chat clicado, ou null ao desmarcar o que já estava aberto. */
  onSelectChat: (chat: ChatDto | null) => void;
  loadingChats: boolean;
  hasMoreChats: boolean;
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error';
  onLoadMoreChats: (page: number) => void;
  alertasAtendimento: any[];
  alertasChamadas: any[];
  isPlayingAudio: boolean;
  onAtender: (chatId: number) => void;
  onAtenderChamada: (chamadaId: number) => void;
};

const Conversas = ({
  value,
  loadingChats,
  hasMoreChats,
  connectionState,
  selectedKey,
  onSelectChat,
  onLoadMoreChats,
  alertasAtendimento,
  alertasChamadas,
  isPlayingAudio,
  onAtender,
  onAtenderChamada,
}: ConversasProps) => {
  // const [selectedIndex, setSelectedIndex] = React.useState(-1);
  const { user } = useAuth();
  const rascunhos = useRascunhos();

  const qTags = useQuery('tags', async () => {
    const result = await axios.get('/api/marcador/box');
    return result.data?.result;
  });

  // Verificar se existem grupos com configurações de visibilidade ativas
  const { data: hasVisibilityConfig } = useQuery(
    'grupos-visibilidade-check',
    async () => {
      if (user?.papel === 'ADMIN') return false; // ADMINs não veem o aviso

      try {
        const result = await axios.get('/api/usuario_grupo');
        const grupos = result.data?.data || [];
        return grupos.some((g: any) =>
          g.modo_visibilidade && g.modo_visibilidade !== 'permitir_todos'
        );
      } catch (error) {
        return false;
      }
    },
    {
      enabled: user?.papel !== 'ADMIN',
      staleTime: 5 * 60 * 1000, // Cache por 5 minutos
    }
  );

  // A seleção é resolvida pelo próprio objeto do chat, não pela linha clicada:
  // a lista se reordena a cada mensagem recebida e a posição não é confiável.
  const toggleChat = (chat: ChatDto) => {
    const key = chatKey(chat);
    onSelectChat(key != null && key === selectedKey ? null : chat);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    chat: ChatDto,
  ) => {
    let hit = true;
    switch (e.code.toLowerCase()) {
      case 'space':
      case 'enter':
        toggleChat(chat);
        break;
      default:
        hit = false;
    }
    if (hit) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>, chat: ChatDto) => {
    toggleChat(chat);
  };

  // trata mensagens com o nome do atendente embutido
  const regexMensagemComAtendente = /^(\*[^\*]+\*\r?\n)?(.*)$/;
  const conteudo = (s: string) => {
    const m = regexMensagemComAtendente.exec(s);
    if (!m) return s.split('\n')[0];

    return m[2].split('\n')[0];
  };

  useEffect(() => {
    console.log("scroll state changed", hasMoreChats, loadingChats)
  }, [hasMoreChats, loadingChats]);

  return (
    <div className={styles.container} data-testid="container-chats">
      <InfiniteScroll
        pageStart={0}
        loadMore={(page) => {
          // Evitar carregamentos durante a inicialização (só carregar após interação do usuário)
          if (hasMoreChats && !loadingChats) {
            onLoadMoreChats(page);
          }
        }}
        threshold={250}
        hasMore={hasMoreChats && !loadingChats}
        useWindow={false}
      >
        {hasVisibilityConfig && (
          <div style={{ padding: '8px' }}>
            <AlertBox
              variant="info"
            >
              Algumas conversas podem estar ocultas devido às configurações de
              visibilidade entre grupos.
            </AlertBox>
          </div>
        )}
        <AlertasChamada
          value={alertasChamadas}
          isPlayingAudio={isPlayingAudio}
          onAtender={onAtenderChamada}
        />
        <AlertasAtendimento
          value={alertasAtendimento}
          hasFilaChamadas={!!alertasChamadas.length}
          isPlayingAudio={isPlayingAudio}
          onAtender={onAtender}
        />
        {value.map((item: any, index) => {
          const itemKey = chatKey(item);
          return (
          <div
            key={itemKey ?? (item.id != null ? `chat-${item.id}` : `conversa-${index}`)}
            data-testid={`conversa`}
            className={[
              styles.conversa,
              itemKey != null && itemKey === selectedKey ? styles.selected : null,
              item.__fila_accepted ? styles.accepted : null,
            ].join(' ')}
            tabIndex={0}
            onKeyDown={e => handleKeyDown(e, item)}
            onClick={e => handleClick(e, item)}
          >
            {/* <div>{JSON.stringify(item)}</div> */}
            <div className={[styles.coluna1, styles.nome].join(' ')}>
              <HBox style={{ alignItems: 'baseline' }} gap="3px">
                <span
                  className='rr-mask'
                  color={
                    item.cliente?.nome
                      ? 'undefined'
                      : 'var(--tc-color-gray-800)'
                  }
                >
                  {item.cliente?.nome ?? item.nome}
                </span>
                <MarcadoresSm ids={item.cliente?.marcadores} />
              </HBox>
            </div>
            <div className={[styles.coluna2, styles.data].join(' ')}>
              <TextoData tamanho="curto" value={item.ultmsg_data_criacao!} />
            </div>
            <div className={[styles.coluna1, styles.conteudo].join(' ')}>
              {/* Rascunho só existe onde há barra de texto: fora de 'atendimento'
                  o compositor nem é renderizado (ver footer/Footer.tsx). E não
                  aparece na conversa aberta: ali o texto já está no compositor,
                  e vê-lo mudando na lista enquanto se digita só rouba atenção. */}
              {item.estado === 'atendimento' &&
              itemKey &&
              itemKey !== selectedKey &&
              rascunhos[itemKey]?.texto ? (
                <>
                  <span style={{ color: 'var(--tc-color-danger-500)' }}>Rascunho: </span>
                  <span className="rr-mask">{rascunhos[itemKey].texto}</span>
                </>
              ) : item.ultmsg_tipo_mime === 'text/message' ? (
                <Markdown className='rr-mask' value={conteudo(item.ultmsg_conteudo_ou_url)} />
              ) : (
                '(mídia)'
              )}
            </div>
            <div className={[styles.coluna2, styles.estado].join(' ')}>
              {item.ultmsg_origem !== 'usuario' && iconeStatus[item.estado]}
              {item.estado === 'atendimento' && item.ultmsg_origem === 'usuario' && !item.ultmsg_metadados?.blacklisted && (
                <div className={styles.naoRespondidoBg}>
                  <Icon
                    path={mdiAccountAlert}
                    size={1.25}
                    color="var(--tc-color-danger-500)"
                  />
                </div>
              )}
            </div>
            <div className={styles.coluna} style={{ scrollbarColor: 'var(--tc-color-gray-200) transparent', display: 'flex', gap: 4, overflowX: 'auto', overflowY: 'hidden', whiteSpace: 'nowrap', minWidth: 0, maxWidth: '100%', scrollbarWidth: 'thin' }}>
              {['chatbot'].includes(item.estado) && (
                <>
                  {item.usuario_responsavel_nome && (
                    <Badge variant="secondary" data-testid='badge-atendente' style={{ flexShrink: 0 }}>
                      <Icon path={mdiHeadset} size={0.75} />
                      {item.usuario_responsavel_nome}
                    </Badge>
                  )}
                  <Badge variant="info" data-testid="badge-chatbot" style={{ flexShrink: 0 }}>
                    <Icon path={mdiRobot} size={0.75} />
                    Bot em ação
                  </Badge>
                </>
              )}
              {['atendimento'].includes(item.estado) && (
                <Badge variant="warning" data-testid='badge-atendente' style={{ flexShrink: 0 }}>
                  <Icon path={mdiHeadset} size={0.75} />
                  {item.usuario_responsavel_nome}
                </Badge>
              )}
              {['finalizado'].includes(item.estado) &&
                item.usuario_responsavel_nome && (
                  <Badge variant="secondary" data-testid='badge-finalizado' style={{ flexShrink: 0 }}>
                    <Icon path={mdiHeadset} size={0.75} />
                    {item.usuario_responsavel_nome}
                  </Badge>
                )}
              <BadgeMarcadores marcadores={item.marcadores} style={{ flexShrink: 0 }} />
            </div>
          </div>
          );
        })}

        {connectionState === 'connecting' && (
          <div className={styles.loadStatus}>
            <Icon
              path={mdiLoading}
              size={1}
              color="var(--tc-color-gray-500)"
              spin={1}
            />
            <span>Conectando...</span>
          </div>
        )}

        {connectionState === 'connected' && loadingChats && (
          <div className={styles.loadStatus}>
            <Icon
              path={mdiLoading}
              size={1}
              color="var(--tc-color-gray-500)"
              spin={1}
            />
            <span>Obtendo lista...</span>
          </div>
        )}

        {connectionState === 'connected' && !loadingChats && !hasMoreChats && (
          <div className={styles.loadStatus}>
            <Icon
              path={mdiCheckAll}
              size={1}
              color="var(--tc-color-gray-500)"
            />
            <span>Isso é tudo</span>
          </div>
        )}

        {connectionState === 'error' && (
          <div className={styles.loadStatus}>
            <Icon
              path={mdiAlertCircle}
              size={0.7}
              color="var(--tc-color-warning)"
            />
            <span>Erro</span>
          </div>
        )}
      </InfiniteScroll>
    </div>
  );
};

export default Conversas;
